<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

declare global {
  interface Window {
    __artworks: Artwork[]
    __baseUrl: string
    __baseTitle: string
  }
}

interface Artwork {
  sqid: string
  title: string
  documentTitle: string
  url: string
  image: string
  thumb: string
  download: string
  info: string
  dimensions: string | null
  media: string | null
  year: number | null
  hasVideo: boolean
  video: string | null
  videoPoster: string | null
}

const artworks = ref<Artwork[]>([])
const currentSqid = ref<string | null>(null)
const showVideo = ref(false)
const videoPlayer = ref<HTMLElement | null>(null)
const dialog = ref<HTMLElement | null>(null)
const closeButton = ref<HTMLButtonElement | null>(null)

const FOCUSABLE = 'a[href], button:not([disabled])'

// The grid link that opened the lightbox, so focus can go back to it on close.
let trigger: HTMLElement | null = null

// Prev/next live inside the fade transition, so navigating destroys the link
// that was focused. Remember which one it was and restore it once the new
// artwork has finished entering.
let pendingArrow: 'prev' | 'next' | null = null

function fitVideoPlayer(event: Event) {
  const video = event.target as HTMLVideoElement
  const maxHeight = window.innerHeight * 0.8
  const scale = Math.min(1, maxHeight / video.videoHeight)
  const width = Math.round(video.videoWidth * scale)

  if (videoPlayer.value) {
    videoPlayer.value.style.maxWidth = `${width}px`
    videoPlayer.value.dataset.sized = ''
  }
}

const artworkIndex = computed(() => artworks.value.findIndex((a) => a.sqid === currentSqid.value))

const artwork = computed(() => (artworkIndex.value !== -1 ? artworks.value[artworkIndex.value] : null))

const prevArtwork = computed(() => (artworkIndex.value >= 1 ? artworks.value[artworkIndex.value - 1] : null))

const nextArtwork = computed(() =>
  artworkIndex.value < artworks.value.length - 1 ? artworks.value[artworkIndex.value + 1] : null,
)

// The URL changes as the lightbox opens, navigates and closes, so the document
// title has to follow it. Titles are formatted server-side (App\Support\Values\
// PageTitle) so they match exactly what was rendered into <head> on page load.
//
// This also owns the modal side effects, because the lightbox can be opened
// three ways: a click on the grid, a deep-linked URL on page load, and browser
// history. Driving them off the state keeps all three in step.
watch(currentSqid, async (sqid, previous) => {
  showVideo.value = false

  const title = sqid ? artworks.value.find((a) => a.sqid === sqid)?.documentTitle : window.__baseTitle

  if (title) {
    document.title = title
  }

  const app = document.getElementById('eg-app')

  if (sqid) {
    document.body.classList.add('modal-open')

    // The page behind an opaque overlay must not stay reachable by tab or by
    // screen reader. `inert` handles both at once.
    app?.setAttribute('inert', '')

    if (!previous) {
      await nextTick()
      closeButton.value?.focus()
    }

    return
  }

  document.body.classList.remove('modal-open')
  app?.removeAttribute('inert')

  // Focus cannot be moved into the page while it is still inert.
  await nextTick()
  restoreTriggerFocus()
})

function restoreTriggerFocus() {
  const target = trigger?.isConnected ? trigger : document.querySelector<HTMLElement>('#artwork-grid a[data-sqid]')

  target?.focus()
  trigger = null
}

function restoreArrowFocus() {
  if (!pendingArrow) {
    return
  }

  const arrow = dialog.value?.querySelector<HTMLElement>(`.${pendingArrow}`)

  // At either end of the collection the arrow no longer renders, so the close
  // button is the nearest sensible landing place.
  ;(arrow ?? closeButton.value)?.focus()
  pendingArrow = null
}

function open(sqid: string) {
  currentSqid.value = sqid

  const baseUrl = window.__baseUrl ?? '/'
  history.replaceState({}, '', `${baseUrl}/${sqid}`)
}

function close() {
  currentSqid.value = null
  showVideo.value = false

  // Server-rendered detail for the deep-linked artwork. The URL is about to go
  // back to the artist page, so the block no longer belongs on it.
  document.getElementById('artwork-detail')?.remove()

  const baseUrl = window.__baseUrl ?? '/'
  history.replaceState({}, '', baseUrl)
}

function navigate(target: Artwork | null) {
  if (!target) {
    return
  }

  const active = document.activeElement

  pendingArrow = active?.classList.contains('prev') ? 'prev' : active?.classList.contains('next') ? 'next' : null

  open(target.sqid)
}

function trapFocus(event: KeyboardEvent) {
  const items = [...(dialog.value?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])].filter(
    (el) => el.getClientRects().length > 0,
  )

  if (!items.length) {
    return
  }

  const first = items[0]
  const last = items[items.length - 1]

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()

    return
  }

  if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

function handleKeyboard(event: KeyboardEvent) {
  if (!currentSqid.value) {
    return
  }

  if (event.key === 'Escape') {
    close()
  } else if (event.key === 'Tab') {
    trapFocus(event)
  } else if (event.key === 'ArrowLeft') {
    navigate(prevArtwork.value)
  } else if (event.key === 'ArrowRight') {
    navigate(nextArtwork.value)
  }
}

let touchStartX = 0
let touchStartY = 0

function handleTouchStart(event: TouchEvent) {
  touchStartX = event.touches[0].clientX
  touchStartY = event.touches[0].clientY
}

function handleTouchEnd(event: TouchEvent) {
  const dx = event.changedTouches[0].clientX - touchStartX
  const dy = event.changedTouches[0].clientY - touchStartY

  if (Math.abs(dx) < 50 || Math.abs(dy) > Math.abs(dx)) {
    return
  }

  if (dx > 0) {
    navigate(prevArtwork.value)
  } else {
    navigate(nextArtwork.value)
  }
}

function handlePopstate() {
  const baseUrl = window.__baseUrl ?? '/'
  const path = window.location.pathname
  const suffix = path.replace(baseUrl, '').replace(/^\//, '')

  if (suffix) {
    const match = artworks.value.find((a) => a.sqid === suffix)
    if (match) {
      currentSqid.value = match.sqid
      return
    }
  }

  currentSqid.value = null
}

onMounted(() => {
  artworks.value = window.__artworks ?? []

  document.addEventListener('keydown', handleKeyboard)
  window.addEventListener('popstate', handlePopstate)

  document.querySelectorAll('#artwork-grid a[data-sqid]').forEach((el) => {
    el.addEventListener('click', (e) => {
      e.preventDefault()
      const sqid = (el as HTMLElement).dataset.sqid
      if (sqid) {
        trigger = el as HTMLElement
        open(sqid)
      }
    })
  })

  handlePopstate()
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyboard)
  window.removeEventListener('popstate', handlePopstate)
  document.getElementById('eg-app')?.removeAttribute('inert')
})
</script>

<template>
  <Teleport to="body">
    <div
      v-if="artwork"
      ref="dialog"
      class="artwork view"
      :class="{ 'is-video': showVideo }"
      role="dialog"
      aria-modal="true"
      :aria-label="artwork.title"
      @click.self="close"
    >
      <div class="visual">
        <Transition name="artwork-fade" mode="out-in" @after-enter="restoreArrowFocus">
          <div
            :key="artworkIndex"
            class="image"
            :class="{ 'video-active': showVideo }"
            @touchstart.passive="handleTouchStart"
            @touchend="handleTouchEnd"
          >
            <a
              v-if="prevArtwork"
              class="prev"
              :href="prevArtwork.url"
              :aria-label="`Vorige: ${prevArtwork.title}`"
              @click.prevent="navigate(prevArtwork)"
            />

            <figure>
              <img v-show="!showVideo" :src="artwork.image" :alt="artwork.title" />
              <video-player v-if="showVideo && artwork.video" ref="videoPlayer">
                <video-minimal-skin>
                  <video
                    :src="artwork.video"
                    :poster="artwork.videoPoster ?? undefined"
                    autoplay
                    playsinline
                    @loadedmetadata="fitVideoPlayer"
                  />
                </video-minimal-skin>
              </video-player>
            </figure>

            <button v-if="showVideo" type="button" class="close-video" @click="showVideo = false">close video</button>

            <a
              v-if="nextArtwork"
              class="next"
              :href="nextArtwork.url"
              :aria-label="`Volgende: ${nextArtwork.title}`"
              @click.prevent="navigate(nextArtwork)"
            />
          </div>
        </Transition>
      </div>

      <aside>
        <Transition name="aside-fade" mode="out-in">
          <div :key="artworkIndex">
            <h3>
              {{ artwork.title }}
              <span v-if="artwork.year" class="year">{{ artwork.year }}</span>
            </h3>

            <dl>
              <div v-if="artwork.dimensions">
                <dt class="visually-hidden">afmetingen</dt>
                <dd>{{ artwork.dimensions }}</dd>
              </div>
              <div v-if="artwork.media">
                <dt class="visually-hidden">media</dt>
                <dd>{{ artwork.media }}</dd>
              </div>
            </dl>

            <div class="info">
              <a :href="artwork.info">informatie aanvragen</a>
            </div>

            <div v-if="artwork.hasVideo" class="video-poster">
              <button type="button" @click="showVideo = !showVideo">
                <img
                  :src="showVideo ? artwork.thumb : String(artwork.videoPoster)"
                  :alt="showVideo ? artwork.title : 'Video'"
                />
                <span v-if="!showVideo" class="play-icon" />
              </button>
            </div>

            <div class="download">
              <a :href="artwork.download" aria-label="Download deze foto" title="download deze foto">
                <span class="icon" />
              </a>
            </div>
          </div>
        </Transition>
      </aside>

      <button ref="closeButton" type="button" class="close" aria-label="Sluiten" @click="close" />
    </div>
  </Teleport>
</template>
